import { View, ScrollView, Text, Pressable } from 'react-native'
import KPICard from '@/components/Charts/KPICard'
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { DateTime } from 'luxon';
import { dayContainer } from '@/utils/reportDates';
import { useState, useMemo, useEffect } from 'react';
import AnalyticsDateRangeControls from '@/components/Charts/AnalyticsDateRangeControls';
import usePlatform from '@/utils/hooks/usePlatform';
import { ActivityIndicator } from 'react-native-paper';
import { Table } from '@/components/General/Table';
import Avatar from '@/components/People/Avatar';
import { useRouter } from 'expo-router';
import DataExporter from '@/utils/DataExporter';
import SolidButton from '@/components/General/SolidButton';
import Chart from '@/components/Charts/Chart';
import SheetModal from '@/components/General/SheetModal';
import Checkbox from '@/components/General/inputs/Checkbox';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SelectInput from '@/components/General/inputs/SelectInput';
import Unicon from '@/components/General/Unicon';
import ErrorHandler from '@/utils/ErrorHandler';

const parseAmount = (value) => {
    const parsed = typeof value === 'number'
        ? value
        : Number(String(value ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const toCents = (value) => Math.round(parseAmount(value) * 100);

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(parseAmount(amount));
};

const formatTransactionDate = (transaction) => {
    for (const value of [transaction?.createdAt, transaction?.transactionDate]) {
        if (!value) continue;
        const date = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(String(value));
        if (date.isValid) return date.toLocaleString(DateTime.DATETIME_MED);
    }
    return '-';
};

const getPersonName = (transaction) => {
    if (!transaction?.Person) return 'Unknown Person';
    return `${transaction.Person.firstName ?? ''} ${transaction.Person.lastName ?? ''}`.trim() || 'Unknown Person';
};

const getFundName = (fundTransaction) => fundTransaction?.Fund?.name?.trim() || 'Unknown Fund';
const getMethodName = (transaction) => transaction?.Method?.name?.trim() || 'Unknown Method';

const formatCount = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const ChartBreakdownModal = ({ breakdown, onClose }) => (
    <SheetModal
        title={breakdown?.title || 'Chart data'}
        isOpen={!!breakdown}
        onCancel={onClose}
        width={760}
    >
        <ScrollView horizontal contentContainerStyle={styles.breakdownTable} testID="contributions-chart-breakdown">
            <View style={styles.breakdownRow}>
                {breakdown?.columns?.map((column, index) => (
                    <Text key={`${column.key}-${index}`} style={styles.breakdownHeader(index === 0)}>{column.label}</Text>
                ))}
            </View>
            {breakdown?.rows?.map((row, rowIndex) => (
                <View key={rowIndex} style={styles.breakdownRow}>
                    {breakdown.columns.map((column, index) => (
                        <Text key={`${column.key}-${index}`} selectable style={styles.breakdownCell(index === 0, rowIndex % 2 === 0)}>
                            {column.format === 'currency' ? formatCurrency(row[index]) : column.format === 'number' ? formatCount(row[index]) : String(row[index] ?? '-')}
                        </Text>
                    ))}
                </View>
            ))}
        </ScrollView>
    </SheetModal>
);

const SectionHeader = ({ title, subtitle }) => (
    <View style={styles.sectionHeader}>
        <View style={styles.sectionAccent} />
        <View style={styles.sectionHeaderText}>
            <Text style={styles.sectionHeaderTitle}>{title}</Text>
            {subtitle ? <Text style={styles.sectionSubtitle}>{subtitle}</Text> : null}
        </View>
    </View>
);

const TABS = [
    { key: 'trends', label: 'Trends' },
    { key: 'byDate', label: 'By Date' },
    { key: 'transactions', label: 'Transactions' },
];

const DATE_PRESETS = [
    { label: 'This Week', value: 'this_week' },
    { label: 'Last Week', value: 'last_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Month to Date', value: 'month_to_date' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'This Quarter', value: 'this_quarter' },
    { label: 'Last Quarter', value: 'last_quarter' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Year to Date', value: 'year_to_date' },
    { label: 'Last Year', value: 'last_year' },
    { label: 'Past 30 Days', value: 'last_30_days' },
    { label: 'Past 90 Days', value: 'last_90_days' },
    { label: 'Past 12 Months', value: 'last_12_months' },
];

// Dates are day containers: device-local midnight of the org-local calendar day.
const getPresetDates = (preset, timeZone) => {
    const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
    switch (preset) {
        case 'month_to_date':
            return { start: dayContainer(now.startOf('month')), end: dayContainer(now) };
        case 'year_to_date':
            return { start: dayContainer(now.startOf('year')), end: dayContainer(now) };
        case 'this_week':
            return { start: dayContainer(now.startOf('week')), end: dayContainer(now.endOf('week')) };
        case 'last_week':
            return { start: dayContainer(now.minus({ weeks: 1 }).startOf('week')), end: dayContainer(now.minus({ weeks: 1 }).endOf('week')) };
        case 'this_month':
            return { start: dayContainer(now.startOf('month')), end: dayContainer(now.endOf('month')) };
        case 'last_month':
            return { start: dayContainer(now.minus({ months: 1 }).startOf('month')), end: dayContainer(now.minus({ months: 1 }).endOf('month')) };
        case 'this_quarter':
            return { start: dayContainer(now.startOf('quarter')), end: dayContainer(now.endOf('quarter')) };
        case 'last_quarter':
            return { start: dayContainer(now.minus({ quarters: 1 }).startOf('quarter')), end: dayContainer(now.minus({ quarters: 1 }).endOf('quarter')) };
        case 'this_year':
            return { start: dayContainer(now.startOf('year')), end: dayContainer(now.endOf('year')) };
        case 'last_year':
            return { start: dayContainer(now.minus({ years: 1 }).startOf('year')), end: dayContainer(now.minus({ years: 1 }).endOf('year')) };
        case 'last_30_days':
            return { start: dayContainer(now.minus({ days: 30 })), end: dayContainer(now) };
        case 'last_90_days':
            return { start: dayContainer(now.minus({ days: 90 })), end: dayContainer(now) };
        case 'last_12_months':
            return { start: dayContainer(now.minus({ months: 12 })), end: dayContainer(now) };
        default:
            return null;
    }
};

const ExportOptionsModal = ({ isOpen, onClose, onConfirm }) => {
    const [format, setFormat] = useState('csv');
    const [exportFundAmountsAsRows, setExportFundAmountsAsRows] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const handleConfirm = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const success = await onConfirm({ format, exportFundAmountsAsRows });
            if (success !== false) onClose();
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <SheetModal
            title="Export Options"
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            cancelText="Cancel"
            confirmText="Export"
            loading={isExporting}
            onConfirm={handleConfirm}
        >
            <View style={styles.exportSheetContent}>
                <Text style={styles.exportSectionLabel}>Format</Text>
                <View style={styles.exportFormatRow}>
                    {[
                        { value: 'csv', label: 'CSV', detail: 'Spreadsheet-ready data', icon: 'document-text-outline' },
                        { value: 'pdf', label: 'PDF', detail: 'Printable transaction report', icon: 'reader-outline' },
                    ].map(option => {
                        const selected = format === option.value;
                        return (
                            <Pressable
                                key={option.value}
                                testID={`contributions-export-format-${option.value}`}
                                accessibilityRole="radio"
                                accessibilityLabel={`${option.label} format`}
                                accessibilityState={{ selected }}
                                style={styles.exportFormatOption(selected)}
                                onPress={() => setFormat(option.value)}
                                disabled={isExporting}
                            >
                                <Unicon name={option.icon} size={20} colorVariant={selected ? 'giving' : 'textSecondary'} />
                                <View style={styles.exportFormatText}>
                                    <Text style={styles.exportFormatLabel(selected)}>{option.label}</Text>
                                    <Text style={styles.exportFormatDetail}>{option.detail}</Text>
                                </View>
                            </Pressable>
                        );
                    })}
                </View>
                {format === 'csv' && (
                    <Pressable
                        testID="contributions-export-funds-as-rows"
                        style={styles.exportOptionRow}
                        onPress={() => setExportFundAmountsAsRows(!exportFundAmountsAsRows)}
                        disabled={isExporting}
                    >
                        <Checkbox
                            checked={exportFundAmountsAsRows}
                            onChange={() => setExportFundAmountsAsRows(!exportFundAmountsAsRows)}
                            disabled={isExporting}
                            size={20}
                        />
                        <View style={styles.exportOptionText}>
                            <Text style={styles.exportOptionLabel}>Export Fund Amounts as Rows</Text>
                            <Text style={styles.exportOptionHelpText}>
                                Splits each transaction into multiple rows — one per fund — so fund names and amounts appear on their own row instead of being grouped in a single column.
                            </Text>
                        </View>
                    </Pressable>
                )}
            </View>
        </SheetModal>
    );
};

const TransactionSummary = ({ summary }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <View style={styles.summaryContainer}>
            <View style={styles.summaryTopRow}>
                <View style={styles.summaryOverall}>
                    <Text style={styles.summaryLabel}>Filtered total</Text>
                    <Text style={styles.summaryTotal}>{formatCurrency(summary.overall)}</Text>
                    <Text style={styles.summaryCount}>{summary.transactionCount} {summary.transactionCount === 1 ? 'transaction' : 'transactions'}</Text>
                </View>
                <Pressable
                    testID="contributions-transactions-summary-toggle"
                    accessibilityRole="button"
                    accessibilityLabel={expanded ? 'Hide totals breakdown' : 'Show totals breakdown'}
                    accessibilityState={{ expanded }}
                    onPress={() => setExpanded(value => !value)}
                    style={styles.summaryToggle}
                >
                    <Text style={styles.summaryToggleText}>{expanded ? 'Hide breakdown' : 'Show breakdown'}</Text>
                    <Unicon name={expanded ? 'chevron-up' : 'chevron-down'} size={15} colorVariant="textSecondary" />
                </Pressable>
            </View>
            {expanded && (
                <View style={styles.summaryBreakdown}>
                    {[
                        { title: 'By Fund', items: summary.funds },
                        { title: 'By Method', items: summary.methods },
                    ].map(section => (
                        <View key={section.title} style={styles.summaryColumn}>
                            <Text style={styles.summaryColumnTitle}>{section.title}</Text>
                            {section.items.length ? section.items.map(item => (
                                <View key={item.key} style={styles.summaryItem}>
                                    <Text numberOfLines={1} style={styles.summaryItemLabel}>{item.label}</Text>
                                    <Text style={styles.summaryItemAmount}>{formatCurrency(item.amount)}</Text>
                                </View>
                            )) : <Text style={styles.summaryEmpty}>No totals</Text>}
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
};

export default function ContributionsDashboard() {
    const currentYear = DateTime.now().year;

    const [activeTab, setActiveTab] = useState('byDate');
    const { data: organization } = Query.organization.byId({});
    const orgTimezone = organization?.timezone;
    const [datesInitialized, setDatesInitialized] = useState(false);
    const [startDate, setStartDate] = useState(() => dayContainer(DateTime.now().startOf('month')));
    const [endDate, setEndDate] = useState(() => dayContainer(DateTime.now()));
    const [selectedPreset, setSelectedPreset] = useState('month_to_date');
    const [searchText, setSearchText] = useState('');

    // Transactions tab filters — rendered through the Table's built-in <TableFilters>
    const buildDefaultTransactionFilters = () => ([
        { id: 'date', label: 'Date', inputType: 'daterange', value: { start: DateTime.now().startOf('month').toJSDate(), end: DateTime.now().toJSDate() } },
        { id: 'batch', label: 'Batch', inputType: 'sheetselector', sheetType: 'contributionBatches', value: null },
        { id: 'methods', label: 'Methods', inputType: 'sheetselector', sheetType: 'contributionMethods', multiple: true, value: [] },
        { id: 'funds', label: 'Funds', inputType: 'sheetselector', sheetType: 'contributionFunds', multiple: true, value: [] },
        { id: 'amount', label: 'Amount', inputType: 'numberrange', value: { min: '', max: '' } },
    ]);
    const [transactionFilters, setTransactionFilters] = useState(buildDefaultTransactionFilters);

    const handleClearTransactionFilters = () => setTransactionFilters(buildDefaultTransactionFilters());
    const [exportOptionsModalOpen, setExportOptionsModalOpen] = useState(false);
    const [chartBreakdown, setChartBreakdown] = useState(null);

    // Trends tab filters
    const [selectedYears, setSelectedYears] = useState([currentYear, currentYear - 1]);
    const [trendsPeriod, setTrendsPeriod] = useState('month');
    const [trendsMethod, setTrendsMethod] = useState(null);
    const [trendsFund, setTrendsFund] = useState(null);

    const { isWeb, isPortrait, isNative } = usePlatform();
    const { theme: { colors, fonts } } = useUnistyles();
    const router = useRouter();

    useEffect(() => {
        if (datesInitialized || !orgTimezone) return;
        const dates = getPresetDates('month_to_date', orgTimezone);
        setStartDate(dates.start);
        setEndDate(dates.end);
        setDatesInitialized(true);
    }, [orgTimezone, datesInitialized]);

    const handlePresetChange = (preset) => {
        const dates = getPresetDates(preset, orgTimezone);
        if (!dates) return;
        setSelectedPreset(preset);
        setStartDate(dates.start);
        setEndDate(dates.end);
    };

    // Available years for the Trends multiselect
    const { data: availableYears } = Query.contributionAnalytics.availableYears();

    // Param contract: comma-separated 4-digit years, ascending (e.g. "2025,2026")
    const yearsParam = useMemo(
        () => [...selectedYears].sort((a, b) => a - b).join(','),
        [selectedYears]
    );

    // Trends chart filter params (single-select; 'all' when none chosen)
    const trendsMethodParam = trendsMethod?.id || 'all';
    const trendsFundParam = trendsFund?.id || 'all';

    // Format dates for queries
    // Day strings — data layer converts to org-local range bounds
    const formattedStartDate = startDate ? DateTime.fromJSDate(startDate).toISODate() : null;
    const formattedEndDate = endDate ? DateTime.fromJSDate(endDate).toISODate() : null;

    // Queries
    const { data: kpis, isLoading: kpisLoading } = Query.contributionAnalytics.kpis({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        options: { enabled: !!formattedStartDate && !!formattedEndDate }
    });

    // Derive transaction filter values from the filters array
    const txnFilter = useMemo(() => {
        const get = (id) => transactionFilters.find(f => f.id === id)?.value;
        return {
            date: get('date') || {},
            batch: get('batch'),
            methodIds: (get('methods') || []).map(m => m?.id).filter(Boolean),
            fundIds: (get('funds') || []).map(f => f?.id).filter(Boolean),
            amount: get('amount') || {},
        };
    }, [transactionFilters]);

    // Date drives the server query; fall back to the current month if cleared
    const orgNow = orgTimezone ? DateTime.now().setZone(orgTimezone) : DateTime.now();
    const txnStartDate = txnFilter.date.start ? DateTime.fromJSDate(txnFilter.date.start).toISODate() : orgNow.startOf('month').toISODate();
    const txnEndDate = txnFilter.date.end ? DateTime.fromJSDate(txnFilter.date.end).toISODate() : orgNow.toISODate();

    const { data: transactions, isLoading: transactionsLoading } = Query.contributionAnalytics.transactions({
        startDate: txnStartDate,
        endDate: txnEndDate,
        batchID: txnFilter.batch?.id,
        methodIds: txnFilter.methodIds,
        amount: txnFilter.amount,
    });

    // Table columns (mirroring ContributionsScreen)
    const tableColumns = useMemo(() => [
        { value: 'Person', renderer: (item) => {
            if (!item.Person) return <Text style={{ fontFamily: fonts.body, color: colors.textSecondary }}>Unknown Person</Text>;

            return (
                <Pressable onPress={() => router.push({ pathname: `/people/${item.Person.id}` })} style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                    <Avatar profile={item.Person} size={35} disableBorder />
                    <View>
                        <Text style={{ fontFamily: fonts.bold, color: colors.text }}>{item.Person.firstName} {item.Person.lastName}</Text>
                        {!!item.Person.email && <Text style={{ color: colors.textSecondary, fontSize: 12 }}>{item.Person.email}</Text>}
                    </View>
                </Pressable>
            );
        } },
        { value: 'Method', renderer: (item) => (
            <Text style={{ fontFamily: fonts.body, color: item.Method?.name ? colors.text : colors.textSecondary }}>{getMethodName(item)}</Text>
        ) },
        { value: 'Details', renderer: (item) => {
            return (
                <View>
                    <Text style={{ fontFamily: fonts.body, color: colors.text }}>{item.Method?.detailName || '-'}</Text>
                    <Text style={{ fontFamily: fonts.body, color: colors.textSecondary, fontSize: 12 }}>{item.methodDetailValue ?? '-'}</Text>
                </View>
            );
        } },
        { value: 'Funds', renderer: (item) => {
            return (
                <View>
                    {item.Funds?.length ? item.Funds.map((fundTransaction, index) => (
                        <View key={fundTransaction.Fund?.id || `unknown-fund-${index}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingBottom: 2, paddingHorizontal: 8, borderRadius: 5, backgroundColor: colors.givingContainer, margin: 2, alignSelf: 'flex-start' }}>
                            <Text style={{ color: colors.giving }}>{getFundName(fundTransaction)}</Text>
                            <Text style={{ color: colors.textSecondary, fontSize: 12 }}>{formatCurrency(fundTransaction.amount)}</Text>
                        </View>
                    )) : <Text style={{ color: colors.textSecondary }}>-</Text>}
                </View>
            );
        } },
        { value: 'Total Amount', renderer: (item) => {
            return (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                    <Text style={{ fontFamily: fonts.body, color: colors.success }}>{formatCurrency(item.amount)}</Text>
                </View>
            );
        } },
        { value: 'Date', renderer: (item) => {
            return formatTransactionDate(item);
        } },
    ], [colors, fonts, router]);

    // Filter transactions by method/fund/amount (client-side) + search text
    const filteredTransactions = useMemo(() => {
        if (!transactions) return [];

        const { methodIds, fundIds, amount } = txnFilter;
        const min = amount.min !== '' && amount.min != null ? parseFloat(amount.min) : null;
        const max = amount.max !== '' && amount.max != null ? parseFloat(amount.max) : null;
        const search = searchText.toLowerCase();

        return transactions.filter(t => {
            if (methodIds.length && !methodIds.includes(t.methodID)) return false;
            if (fundIds.length && !t.Funds?.some(f => fundIds.includes(f.Fund?.id))) return false;

            const amt = parseFloat(t.amount || 0);
            if (min != null && amt < min) return false;
            if (max != null && amt > max) return false;

            if (search) {
                const giverName = t.Person ? `${t.Person.firstName} ${t.Person.lastName}`.toLowerCase() : '';
                const methodName = t.Method?.name?.toLowerCase() || '';
                const fundNames = t.Funds?.map(f => f.Fund?.name?.toLowerCase()).join(' ') || '';
                if (!(giverName.includes(search) || methodName.includes(search) || fundNames.includes(search) || String(t.amount ?? '').includes(search))) return false;
            }
            return true;
        });
    }, [transactions, searchText, txnFilter]);

    // Calculate filtered totals
    const transactionSummary = useMemo(() => {
        const fundTotals = new Map();
        const methodTotals = new Map();
        let overallCents = 0;

        const addTotal = (map, key, label, amountCents) => {
            const current = map.get(key);
            if (current) {
                current.amountCents += amountCents;
            } else {
                map.set(key, { key, label, amountCents });
            }
        };

        filteredTransactions.forEach(transaction => {
            const transactionCents = toCents(transaction.amount);
            overallCents += transactionCents;

            const methodLabel = getMethodName(transaction);
            const methodKey = transaction.Method?.id ? `method-${transaction.Method.id}` : 'method-unknown';
            addTotal(methodTotals, methodKey, methodLabel, transactionCents);

            let allocatedCents = 0;
            (transaction.Funds || []).forEach((fundTransaction) => {
                const allocationCents = toCents(fundTransaction.amount);
                allocatedCents += allocationCents;
                const fundLabel = getFundName(fundTransaction);
                const fundKey = fundTransaction.Fund?.id
                    ? `fund-${fundTransaction.Fund.id}`
                    : 'fund-unknown';
                addTotal(fundTotals, fundKey, fundLabel, allocationCents);
            });

            const differenceCents = transactionCents - allocatedCents;
            if (Math.abs(differenceCents) >= 1) {
                addTotal(fundTotals, 'fund-unallocated', 'Unallocated', differenceCents);
            }
        });

        const sortedTotals = (map) => Array.from(map.values())
            .map(item => ({ ...item, amount: item.amountCents / 100 }))
            .sort((a, b) => b.amountCents - a.amountCents || a.label.localeCompare(b.label));

        return {
            overall: overallCents / 100,
            transactionCount: filteredTransactions.length,
            funds: sortedTotals(fundTotals),
            methods: sortedTotals(methodTotals),
        };
    }, [filteredTransactions]);

    const exportTransactionsToCSV = async (options = {}) => {
        let rows = [];
        if (options.exportFundAmountsAsRows) {
            for (const t of filteredTransactions) {
                const base = {
                    "transactionID": t.id,
                    "batchID": txnFilter.batch?.id ?? '',
                    "batchName": txnFilter.batch?.name ?? '',
                    "payeeID": t.Person?.id ?? '',
                    "payeeName": t.Person ? `${t.Person.firstName ?? ''} ${t.Person.lastName ?? ''}`.trim() : 'Unknown',
                    "method": t.Method?.name ?? 'Unknown',
                    "methodDetailName": t.Method?.detailName ?? '',
                    "methodDetailValue": t.methodDetailValue ?? '',
                    "transactionTotal": t.amount,
                    "date": formatTransactionDate(t),
                    "note": t.note ?? ''
                };
                const funds = t.Funds?.filter(f => f.Fund);
                if (funds?.length > 0) {
                    for (const f of funds) {
                        rows.push({ ...base, "fundName": f.Fund.name, "fundAmount": f.amount });
                    }
                } else {
                    rows.push({ ...base, "fundName": '', "fundAmount": '' });
                }
            }
        } else {
            rows = filteredTransactions.map(t => ({
                "transactionID": t.id,
                "batchID": txnFilter.batch?.id ?? '',
                "batchName": txnFilter.batch?.name ?? '',
                "payeeID": t.Person?.id ?? '',
                "payeeName": t.Person ? `${t.Person.firstName ?? ''} ${t.Person.lastName ?? ''}`.trim() : 'Unknown',
                "method": t.Method?.name ?? 'Unknown',
                "methodDetailName": t.Method?.detailName ?? '',
                "methodDetailValue": t.methodDetailValue ?? '',
                "funds": t.Funds?.map(f => f.Fund ? `${f.Fund.name},${f.amount}` : '').filter(Boolean).join("; "),
                "transactionTotal": t.amount,
                "date": formatTransactionDate(t),
                "note": t.note ?? ''
            }));
        }
        const timestamp = DateTime.now().toFormat('MMddyyyyHHmmss');
        return await DataExporter.exportToCSV(rows, `reach_transactions_${timestamp}`);
    };

    const exportTransactionsToPDF = async () => {
        const rows = filteredTransactions.map(transaction => ({
            "Person": [getPersonName(transaction), transaction.Person?.email].filter(Boolean).join('\n'),
            "Method": getMethodName(transaction),
            "Details": [transaction.Method?.detailName || '-', transaction.methodDetailValue ?? '-'].join('\n'),
            "Funds": transaction.Funds?.length
                ? transaction.Funds.map(fund => `${getFundName(fund)} ${formatCurrency(fund.amount)}`).join('\n')
                : '-',
            "Total Amount": formatCurrency(transaction.amount),
            "Date": formatTransactionDate(transaction),
        }));
        const timestamp = DateTime.now().toFormat('MMddyyyyHHmmss');
        return await DataExporter.exportToPDF(rows, `reach_transactions_${timestamp}`, {
            title: 'Contribution Transactions',
            subtitle: `Filtered report • Generated ${DateTime.now().toLocaleString(DateTime.DATETIME_MED)}`,
            orientation: 'portrait',
            tableStyles: { fontSize: 6.7, cellPadding: 1.2, overflow: 'linebreak', valign: 'middle' },
            headStyles: { fillColor: [5, 150, 105], textColor: 255, fontSize: 7, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [246, 248, 247] },
            margin: { left: 8, right: 8 },
            columnStyles: {
                0: { cellWidth: 35 },
                1: { cellWidth: 22 },
                2: { cellWidth: 28 },
                3: { cellWidth: 43 },
                4: { cellWidth: 26, halign: 'right' },
                5: { cellWidth: 40 },
            },
            summary: {
                title: 'Filtered Summary',
                items: [
                    { label: 'Overall Total', value: formatCurrency(transactionSummary.overall) },
                    { label: 'Transaction Count', value: String(transactionSummary.transactionCount) },
                ],
                sections: [
                    { title: 'By Fund', items: transactionSummary.funds.map(item => ({ label: item.label, value: formatCurrency(item.amount) })) },
                    { title: 'By Method', items: transactionSummary.methods.map(item => ({ label: item.label, value: formatCurrency(item.amount) })) },
                ],
            },
        });
    };

    const handleExportTransactions = async (options) => {
        try {
            if (options.format === 'pdf') return await exportTransactionsToPDF();
            return await exportTransactionsToCSV(options);
        } catch (error) {
            const exportError = error instanceof Error ? error : Error(String(error));
            ErrorHandler(exportError, { query: 'exportContributionTransactions', format: options.format }, 'Something went wrong exporting transactions');
            return false;
        }
    };

    const yearOptions = availableYears?.length ? availableYears : [currentYear, currentYear - 1];

    return (
        <View style={styles.screen}>
            {/* Tab Switcher */}
            <View style={styles.tabsRow}>
                {TABS.map(tab => {
                    const isActive = activeTab === tab.key;
                    return (
                        <Pressable
                            key={tab.key}
                            testID={`contributions-tab-${tab.key}`}
                            onPress={() => setActiveTab(tab.key)}
                            style={styles.tabButton(isActive)}
                        >
                            <Text style={styles.tabLabel(isActive)}>{tab.label}</Text>
                        </Pressable>
                    );
                })}
            </View>

        {activeTab === 'transactions' ? (
            <View style={styles.transactionsTabContent}>
                <Table
                    title="All Transactions"
                    columns={tableColumns}
                    data={filteredTransactions}
                    isLoading={transactionsLoading}
                    onSearchChange={setSearchText}
                    filters={transactionFilters}
                    onFiltersChange={setTransactionFilters}
                    onClearFilters={handleClearTransactionFilters}
                    headerSummaryContent={<TransactionSummary summary={transactionSummary} />}
                    estimatedItemSize={60}
                    headerRightContent={<View style={{alignItems:"flex-end", flexWrap:"wrap", flexDirection:"row", justifyContent:"flex-end", gap:10}}>
                        <SolidButton
                            size="sm"
                            disabled={filteredTransactions?.length === 0}
                            iconName='download'
                            iconBefore
                            variant="outlined"
                            type="giving"
                            width={110}
                            text="Export"
                            testID="contributions-transactions-export"
                            onPress={() => setExportOptionsModalOpen(true)}
                        />
                    </View>}
                />
            </View>
        ) : (
        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
            <View style={styles.hero}>
                <View style={styles.heroIconContainer}>
                    <Unicon name="cash-outline" library="ionicon" colorVariant="onPrimary" size={28} />
                </View>
                <Text style={styles.heroTitle}>Contributions Dashboard</Text>
                <Text style={styles.heroDescription}>
                    Monitor giving trends, giver activity, and fund totals.
                </Text>
            </View>

            {activeTab === 'trends' && (
                <View style={styles.tabContent}>
                    {/* Trends Filters (apply to both charts) */}
                    <View style={styles.trendsFilters}>
                        <View style={styles.trendsYearFilter}>
                            <SelectInput
                                isNotOnSurface
                                multiple
                                hideClearButton
                                hideSelectedPills
                                title="Years"
                                placeholder="Select Years"
                                options={yearOptions.map(year => ({ label: String(year), value: year }))}
                                value={selectedYears}
                                onUpdate={setSelectedYears}
                            />
                        </View>

                        <View>
                            <Text style={styles.filterLabel}>Period</Text>
                            <View style={styles.periodToggle}>
                                {[{ key: 'month', label: 'Month' }, { key: 'quarter', label: 'Quarter' }].map(option => {
                                    const isSelected = trendsPeriod === option.key;
                                    return (
                                        <Pressable
                                            key={option.key}
                                            testID={`contributions-trends-period-${option.key}`}
                                            onPress={() => setTrendsPeriod(option.key)}
                                            style={styles.periodOption(isSelected)}
                                        >
                                            <Text style={styles.periodOptionLabel(isSelected)}>{option.label}</Text>
                                        </Pressable>
                                    );
                                })}
                            </View>
                        </View>

                        <View style={styles.trendsSheetFilter}>
                            <SheetSelector
                                isNotOnSurface
                                titleOnly={!isPortrait}
                                title="Method"
                                type="contributionMethods"
                                placeholder="All Methods"
                                value={trendsMethod}
                                onChange={setTrendsMethod}
                            />
                        </View>

                        <View style={styles.trendsSheetFilter}>
                            <SheetSelector
                                isNotOnSurface
                                titleOnly={!isPortrait}
                                title="Fund"
                                type="contributionFunds"
                                placeholder="All Funds"
                                value={trendsFund}
                                onChange={setTrendsFund}
                            />
                        </View>
                    </View>

                    {/* Trends Charts */}
                    <View style={styles.chartGrid}>
                        <Chart height={isWeb ? 500 : 500} chartName="contributions/TotalGivingTrendsChart" parameters={{ years: yearsParam, period: trendsPeriod, methodId: trendsMethodParam, fundId: trendsFundParam }} onViewData={setChartBreakdown} />
                        <Chart height={isWeb ? 500 : 500} chartName="contributions/TotalGiversTrendsChart" parameters={{ years: yearsParam, period: trendsPeriod, methodId: trendsMethodParam, fundId: trendsFundParam }} onViewData={setChartBreakdown} />
                    </View>
                </View>
            )}

            {activeTab === 'byDate' && (
                <View style={styles.tabContent}>
            {/* Date Range Filters */}
            <AnalyticsDateRangeControls
                presets={DATE_PRESETS}
                selectedPreset={selectedPreset}
                startDate={startDate}
                endDate={endDate}
                onPresetChange={handlePresetChange}
                onStartDateChange={(d) => { setDatesInitialized(true); setSelectedPreset('custom'); setStartDate(d); }}
                onEndDateChange={(d) => { setDatesInitialized(true); setSelectedPreset('custom'); setEndDate(d); }}
                isNative={isNative}
                testIDPrefix="contributions-date"
            />

            {/* KPI Cards */}
            <SectionHeader title="Overview" subtitle="Giving totals for the selected period" />
            <View style={styles.kpiRow}>
                <ScrollView showsHorizontalScrollIndicator={false} horizontal keyboardShouldPersistTaps="handled"
>
                    <View style={styles.kpiGrid}>
                        <KPICard 
                            title="Total Giving" 
                            content={kpisLoading ? <ActivityIndicator /> : formatCurrency(kpis?.totalAmount || 0)}
                        />
                        <KPICard 
                            title="Total Transactions" 
                            content={kpisLoading ? <ActivityIndicator /> : kpis?.totalTransactions ?? 0}
                        />
                        <KPICard 
                            title="Unique Givers" 
                            content={kpisLoading ? <ActivityIndicator /> : kpis?.uniqueGivers ?? 0}
                        />
                        <KPICard 
                            title="Avg. Gift" 
                            content={kpisLoading ? <ActivityIndicator /> : formatCurrency(kpis?.totalTransactions > 0 ? kpis?.totalAmount / kpis?.totalTransactions : 0)}
                        />
                    </View>
                </ScrollView>
            </View>

            {/* Charts Section */}
            <SectionHeader title="Charts" subtitle="Breakdowns for the selected period" />
            <View style={styles.chartGrid}>
                <Chart height={isWeb ? 500 : 400} chartName="contributions/TotalContributionsByMonthChart" parameters={{startDate: DateTime.fromJSDate(startDate).toISODate(), endDate: DateTime.fromJSDate(endDate).toISODate()}} onViewData={setChartBreakdown} />
            </View>
            <View style={styles.chartGrid}>
                <Chart height={isWeb ? 500 : 400} chartName="contributions/GivingByMethodChart" parameters={{startDate: DateTime.fromJSDate(startDate).toISODate(), endDate: DateTime.fromJSDate(endDate).toISODate()}} onViewData={setChartBreakdown} />
                <Chart height={isWeb ? 500 : 400} chartName="contributions/GivingByFundChart" parameters={{startDate: DateTime.fromJSDate(startDate).toISODate(), endDate: DateTime.fromJSDate(endDate).toISODate()}} onViewData={setChartBreakdown} />
            </View>
                </View>
            )}

        </ScrollView>
        )}

            <ChartBreakdownModal breakdown={chartBreakdown} onClose={() => setChartBreakdown(null)} />
            <ExportOptionsModal
                isOpen={exportOptionsModalOpen}
                onClose={() => setExportOptionsModalOpen(false)}
                onConfirm={handleExportTransactions}
            />
        </View>
    )
}

const styles = StyleSheet.create(({colors, fonts, shadows}, {isPortrait}) => ({
    screen: {
        flex: 1,
        padding: 12,
        paddingBottom: 0,
    },
    scrollContent: {
        gap: 16,
        paddingBottom: 40,
    },
    transactionsTabContent: {
        flex: 1,
        marginTop: 16,
        marginHorizontal: -15,
    },
    tabsRow: {
        flexDirection: 'row',
        gap: 4,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    tabButton: (isActive) => ({
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderBottomWidth: 3,
        borderBottomColor: isActive ? colors.primary : 'transparent',
        cursor: 'pointer',
    }),
    tabLabel: (isActive) => ({
        fontSize: 15,
        fontFamily: isActive ? fonts.heading : fonts.body,
        color: isActive ? colors.primary : colors.textSecondary,
    }),
    tabContent: {
        gap: 16,
    },
    trendsFilters: {
        paddingTop: 16,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 24,
        alignItems: 'flex-start',
    },
    filterLabel: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginBottom: 6,
    },
    trendsYearFilter: {
        minWidth: 200,
        flexGrow: isPortrait ? 1 : 0,
    },
    periodToggle: {
        flexDirection: 'row',
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 8,
        overflow: 'hidden',
    },
    periodOption: (isSelected) => ({
        paddingVertical: 8,
        paddingHorizontal: 18,
        backgroundColor: isSelected ? colors.primary : colors.surface,
        cursor: 'pointer',
    }),
    periodOptionLabel: (isSelected) => ({
        fontSize: 14,
        fontFamily: isSelected ? fonts.bold : fonts.body,
        color: isSelected ? colors.onPrimary || '#FFFFFF' : colors.text,
    }),
    trendsSheetFilter: {
        minWidth: 200,
        flexGrow: isPortrait ? 1 : 0,
        // Offset for the missing filterLabel that Years/Period have above their controls
        marginTop: -10,
    },
    // Hero
    hero: {
        alignItems: 'center',
        paddingVertical: 20,
        gap: 12,
    },
    heroIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitle: {
        fontSize: 24,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    heroDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        maxWidth: 500,
        lineHeight: 20,
    },
    // Section headers
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 10,
    },
    sectionAccent: {
        width: 4,
        alignSelf: 'stretch',
        minHeight: 30,
        borderRadius: 4,
        backgroundColor: colors.primary,
    },
    sectionHeaderText: {
        gap: 2,
    },
    sectionHeaderTitle: {
        fontSize: 19,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    sectionSubtitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    kpiGrid: {
        flex: 1,
        flexDirection: 'row',
        gap: 15,
    },
    kpiRow: {
        flexDirection: 'row',
        flexWrap: "wrap",
        gap: 15,
    },
    chartGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    chartPlaceholder: {
        height: 300,
        backgroundColor: colors.surface,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
        boxShadow: shadows.card,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    chartPlaceholderText: {
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 8,
    },
    chartPlaceholderSubtext: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    tableFiltersRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 8,
    },
    tableFilterItem: {
        width: isPortrait ? '100%' : 250,
    },
    summaryContainer: {
        backgroundColor: colors.inputBackground,
        borderRadius: 10,
        paddingHorizontal: 10,
        paddingVertical: 7,
    },
    summaryTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
    },
    summaryOverall: {
        flexDirection: 'row',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: 7,
        flexShrink: 1,
    },
    summaryLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    summaryTotal: {
        color: colors.giving,
        fontFamily: fonts.bold,
        fontSize: 16,
    },
    summaryCount: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 11,
    },
    summaryToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        paddingVertical: 4,
        paddingLeft: 8,
        cursor: 'pointer',
    },
    summaryToggleText: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: 11,
    },
    summaryBreakdown: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
        paddingTop: 7,
        marginTop: 6,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    summaryColumn: {
        flex: 1,
        minWidth: isPortrait ? '100%' : 220,
        gap: 3,
    },
    summaryColumnTitle: {
        color: colors.giving,
        fontFamily: fonts.bold,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 1,
    },
    summaryItem: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        minHeight: 20,
    },
    summaryItemLabel: {
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: 12,
        flex: 1,
    },
    summaryItemAmount: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 12,
    },
    summaryEmpty: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },

    breakdownTable: {
        flexDirection: 'column',
        paddingBottom: 24,
    },
    breakdownRow: {
        flexDirection: 'row',
    },
    breakdownHeader: (first) => ({
        width: first ? 170 : 120,
        padding: 12,
        backgroundColor: colors.givingContainer,
        color: colors.giving,
        fontFamily: fonts.bold,
        textAlign: first ? 'left' : 'right',
    }),
    breakdownCell: (first, shaded) => ({
        width: first ? 170 : 120,
        padding: 12,
        backgroundColor: shaded ? colors.surface : colors.background,
        color: colors.text,
        fontFamily: first ? fonts.body : fonts.bold,
        textAlign: first ? 'left' : 'right',
        fontVariant: first ? undefined : ['tabular-nums'],
    }),

    // Export Options
    exportSheetContent: { paddingBottom: 20, gap: 12 },
    exportSectionLabel: { fontSize: 12, fontFamily: fonts.bold, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 },
    exportFormatRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    exportFormatOption: (selected) => ({ flex: 1, minWidth: 150, flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: selected ? colors.givingContainer : colors.surface, borderRadius: 10, borderWidth: 1, borderColor: selected ? colors.giving : colors.borderSubtle, cursor: 'pointer' }),
    exportFormatText: { flex: 1, gap: 2 },
    exportFormatLabel: (selected) => ({ fontSize: 14, fontFamily: fonts.bold, color: selected ? colors.giving : colors.text }),
    exportFormatDetail: { fontSize: 11, fontFamily: fonts.body, color: colors.textSecondary },
    exportOptionRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 12, backgroundColor: colors.surface, borderRadius: 10, borderWidth: 1, borderColor: colors.borderSubtle, cursor: 'pointer' },
    exportOptionText: { flex: 1, gap: 4 },
    exportOptionLabel: { fontSize: 14, fontFamily: fonts.heading, color: colors.text },
    exportOptionHelpText: { fontSize: 12, fontFamily: fonts.body, color: colors.textSecondary, lineHeight: 18 },
}));
